import { ref, computed, onMounted, onUnmounted } from 'vue';
import { getResults } from '@/services/dashboard/dashboard';

/**
 * Composable de resultados del dashboard.
 * SIN soporte multi-season — trabaja a nivel de división.
 * NO extiende useDashboardTab por ser un endpoint diferente.
 *
 * @returns {{
 *     resultsData: import('vue').Ref<Array>,
 *     loading: import('vue').Ref<boolean>,
 *     error: import('vue').Ref<string|null>,
 *     groupedResults: import('vue').ComputedRef<Array>,
 *     fetchResults: () => Promise<void>,
 * }}
 */
export function useResults() {
    const resultsData = ref([]);
    const loading = ref(false);
    const error = ref(null);

    let abortController = null;

    onUnmounted(() => {
        abortController?.abort();
    });

    /**
     * Resultados agrupados por jornada.
     * @type {import('vue').ComputedRef<Array<{ jornada: number, matches: Array }>>}
     */
    const groupedResults = computed(() => {
        const data = resultsData.value;
        if (!data || data.length === 0) return [];

        // The API returns data grouped by jornada already
        return data.map(group => ({
            jornada: group.jornada,
            matches: (group.matches || []).map(m => ({
                jornada: m.jornada,
                localId: m.local?.id || null,
                localName: m.local?.nombre || '',
                localShield: m.local?.shield || null,
                visitanteId: m.visitante?.id || null,
                visitanteName: m.visitante?.nombre || '',
                visitanteShield: m.visitante?.shield || null,
                golesLocal: m.goles_local,
                golesVisitante: m.goles_visitante,
                date: m.date || '',
                status: m.status || 'pending',
                stadium: m.stadium || '',
            })),
        }));
    });

    async function fetchResults() {
        abortController?.abort();
        abortController = new AbortController();

        loading.value = true;
        error.value = null;

        try {
            const response = await getResults(abortController.signal);
            resultsData.value = response.data?.data || [];
        } catch (err) {
            if (err.name === 'CanceledError' || err.name === 'AbortError') return;
            error.value = err.message || 'Error al cargar los resultados';
            if (process.env.NODE_ENV !== 'production') {
                console.error('Error fetching results:', err);
            }
        } finally {
            loading.value = false;
        }
    }

    onMounted(() => {
        fetchResults();
    });

    return {
        resultsData,
        loading,
        error,
        groupedResults,
        fetchResults,
    };
}
